import { useQuery } from '@tanstack/react-query';

import { deliveryViewerCta } from '@/content/deliveryProof';
import { type DeliveryGalleryItem, deliveryShowcaseQuery } from '@/services/deliveries';

import { type DeliveryViewers, useDeliveryViewers } from './useDeliveryViewers';

export type DeliveryShowcase = {
  items: DeliveryGalleryItem[];
  viewers: DeliveryViewers;
  /**
   * True when there is nothing to show. Every host band reads this and renders
   * nothing at all — which is the whole reason the bands can be added to seven
   * pages before Kabex has supplied a single photograph.
   */
  empty: boolean;
};

/**
 * The newest handovers, and the viewers a tile opens — for every page that is
 * not the gallery.
 *
 * One medium per handover (the source decides which), because a strip of six
 * has six slots and four of them going to one talkative customer's photo set
 * proves far less than four different customers' cars.
 *
 * The tiles here open the same two viewers the gallery does, over the strip's
 * own six items rather than the whole archive. Somebody who taps the third
 * photograph on the home page pages through those six and then has a button to
 * the full wall — not ninety tiles they never asked for.
 */
export function useDeliveryShowcase(limit = 6): DeliveryShowcase {
  const { data } = useQuery(deliveryShowcaseQuery(limit));
  const items = data ?? [];
  const viewers = useDeliveryViewers({ items, action: deliveryViewerCta });

  return { items, viewers, empty: items.length === 0 };
}
